<template>
  <div class="wrapper">
    <div class="rounded-medium py-10 px-4 lg:px-10 mb-4">
      <div class="xl:grid xl:grid-cols-12">
        <div class="xl:col-start-1 xl:col-span-4 flex flex-col">
          <div class="mb-10 xl:mb-0 xl:pr-4 2xl:pr-10 xl:pb-5 grid grid-cols-12 xl:divide-x-0 lg:gap-10 xl:gap-0 items-start">
            <div class="col-span-12 lg:col-span-7 xl:col-span-12 mb-7">
              <div class="bg-gray-600 border border-gray-300 rounded-lg py-7 px-9">
                <div class="md:flex flex-col">
                  <div class="">
                      <label
                          class="block text-gray-200 text-sm sm:text-sm md:text-base mb-2 font-medium"
                          for="matrixName"
                      >
                          Annual Feed Production (MT)
                      </label>
                  </div>
                  <div class="w-full">
                    <vue-numeric 
                        v-model="feedProduction"
                        id="feedProduction"
                        step="1000"
                        currency-symbol-position="suffix"
                        currency="MT"
                        class="appearance-none rounded w-full py-3 px-3 text-grey-darker leading-tight focus:outline-none focus:ring input_style"
                    ></vue-numeric>
                  </div>
                </div>
              </div>

              <div class="mt-10 text-center hidden lg:block xl:hidden">
                <span class="text-grey-dark text-lg">For more info, visit</span>
                <a href="https://www.sustell.com/" target="_blank">
                  <img src="/images/sustell.png" class="mx-auto mt-0.5" alt="sustell">
                </a>
              </div>
            </div>

            <div class="col-span-12 lg:col-span-5 xl:col-span-12 mx-2 mt-4 xl:mt-8">
              <div>
                <div class="mb-2">
                  <img src="/images/goals.png" class="mx-auto">
                </div>
                <div class="flex justify-center">
                    <div class="mx-2 flex-1">
                        <img src="/images/E_WEB_12.png" alt="responsible consumption and production" class="object-contain max-h-full min-w-full">
                    </div>
                    <div class="mx-2 flex-1">
                        <img src="/images/sdg13.png" alt="climate action" class="object-contain max-h-full min-w-full">
                    </div>
                    <div class="mx-2 flex-1">
                        <img src="/images/E_WEB_14.png" alt="life below water" class="object-contain max-h-full min-w-full">
                    </div>
                </div>
              </div>

              <!-- <div class="text-center">
                <div class="grid">
                  <slither-slider :options="{autoplay:true, animatedDots: true, preserveGrid: true}">
                    <div class="bg-white p-1.5">
                      <img src="/images/slide1.png" class="mx-auto">
                    </div>
                    <div class="bg-white p-1.5">
                      <img src="/images/slide2.png" class="mx-auto">
                    </div>
                    <div class="bg-white p-1.5">
                      <img src="/images/slide4.png" class="mx-auto">
                    </div>
                  </slither-slider>
                </div>
              </div> -->

              <div class="mt-7 xl:mt-14 text-center lg:hidden xl:block">
                <span class="text-grey-dark text-lg">For more info, visit</span>
                <a href="https://www.sustell.com/" target="_blank">
                  <img src="/images/sustell.png" class="mx-auto mt-0.5" alt="sustell">
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="xl:col-span-8 divide-y divide-gray-300">
          <div class="flex justify-around md:justify-center items-center pb-8 lg:pb-14">
            <div class="grid md:grid-cols-2 gap-11 xl:ml-4">
              <div class="grid grid-cols-12 bg-gray-600 border border-gray-300 rounded-lg p-5 pb-8">
                <div class="col-span-3 md:col-span-4">
                  <img src="/images/po4-emissions.png" class="mx-auto">
                </div>
                <div class="col-span-8 md:col-span-7 pl-4 content-center flex flex-col justify-center">
                  <p class="text-blue-100 text-lg 2xl:text-2xl mb-1 font-bold">Phosphorus excretion</p>
                  <div class="2xl:flex items-center justify-left">
                    <span class="result text-2xl 2xl:text-4xl mr-2.5 text-brand-100 font-bold">{{ billionNumberHandling(po) }}</span>
                    <span class="text-gray-200 text-xs md:text-sm block">MT of PO<sub>4</sub> <br>equivalent</span>
                  </div>
                </div>
                <div
                    class="col-span-1 img-arrow"
                    :class="{'img-rotate': po < 0}"
                >
                  <img src="/images/arrow.png" class="mx-auto" alt="Sustainable development goals">
                </div>
              </div>
              <div class="grid grid-cols-12 bg-gray-600 border border-gray-300 rounded-lg p-5 pb-8">
                <div class="col-span-3 md:col-span-4">
                  <img src="/images/co2-emissions.png" class="mx-auto">
                </div>
                <div class="col-span-8 md:col-span-7 pl-4 content-center flex flex-col justify-center">
                  <p class="text-blue-100 text-lg 2xl:text-2xl mb-1 font-bold">Greenhouse gas emissions</p>
                  <div class="2xl:flex items-center justify-left">
                    <span class="result text-2xl 2xl:text-4xl mr-2.5 text-brand-100 font-bold">{{ billionNumberHandling(ghgEmissions) }}</span>
                    <span class="text-gray-200 text-xs md:text-sm block">MT of CO<sub>2</sub> <br>equivalent</span>
                  </div>
                </div>
                <div
                    class="col-span-1 img-arrow"
                    :class="{'img-rotate': ghgEmissions < 0}"
                >
                  <img src="/images/arrow.png" class="mx-auto" alt="Sustainable development goals">
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col justify-around md:justify-center items-center xl:space-x-4 2xl:space-x-12 pt-4 lg:pt-8">
            <div class="text-blue-100 font-semibold text-xl mb-6 lg:mb-9">This is equivalent to...</div>

            <div class="grid grid-cols-1 mb-14">
              <div class="grid grid-cols-12 bg-gray-600 border border-gray-300 rounded-lg pb-10 pt-3 lg:py-10 px-8 divide-gray-300 divide-y lg:divide-y-0 lg:divide-x space-y-6 lg:space-y-0">
                <div class="grid grid-cols-1 2xl:grid-cols-12 col-span-12 lg:col-span-4 items-center pt-6 lg:pt-0">
                  <div class="2xl:col-start-2 col-span-3 2xl:col-span-2 justify-end flex mb-2 2xl:mb-0">
                    <img src="/images/car.png" class="mx-auto">
                  </div>
                  <div class="col-span-9 pl-4 content-center flex flex-col justify-center">
                    <p
                        class="text-blue-100 text-base lg:text-sm mb-1 font-bold"
                    >
                      The Greenhouse Gas Emission avoided by
                    </p>
                    <div class="flex items-center justify-left">
                      <span class="result text-2xl lg:text-3xl mr-2.5 text-brand-100 font-bold">{{ billionNumberHandling(car) }}</span>
                    </div>
                    <div class="flex items-center justify-left">
                      <span class="text-xs mr-2.5 text-grey-200">Miles driven by an average passenger vehicle</span>
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-1 2xl:grid-cols-12 col-span-12 lg:col-span-4 items-center pt-6 lg:pt-0">
                  <div class="2xl:col-start-2 col-span-3 2xl:col-span-2 justify-end flex mb-2 2xl:mb-0">
                    <img src="/images/phone.png" class="mx-auto">
                  </div>
                  <div class="col-span-9 pl-4 content-center flex flex-col justify-center">
                    <p
                        class="text-blue-100 text-base lg:text-sm mb-1 font-bold"
                    >
                      CO<span class="align-sub text-xs">2</span> emissions from
                    </p>
                    <div class="flex items-center justify-left">
                      <span class="result text-2xl lg:text-3xl mr-2.5 text-brand-100 font-bold">{{ billionNumberHandling(phone) }}</span>
                    </div>
                    <div class="flex items-center justify-left">
                      <span class="text-xs mr-2.5 text-grey-200">Number of smartphones charged</span>
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-1 2xl:grid-cols-12 col-span-12 lg:col-span-4 items-center pt-6 lg:pt-0">
                  <div class="2xl:col-start-2 col-span-3 2xl:col-span-2 justify-end flex mb-2 2xl:mb-0">
                    <img src="/images/house.png" class="mx-auto">
                  </div>
                  <div class="col-span-9 pl-4 content-center flex flex-col justify-center">
                    <p
                        class="text-blue-100 text-base lg:text-sm mb-1 font-bold"
                    >
                      CO<span class="align-sub text-xs">2</span> emissions from
                    </p>
                    <div class="flex items-center justify-left">
                      <span class="result text-2xl lg:text-3xl mr-2.5 text-brand-100 font-bold">{{ billionNumberHandling(house) }}</span>
                    </div>
                    <div class="flex items-center justify-left">
                      <span class="text-xs mr-2.5 text-grey-200">Homes' energy use for one year</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import {PRODUCT_FORM_MAP as productFormMap, AQUA_PHOSPOHOROUS_EXCRETION_CONSTANTS as aquaPhospohorousConstants} from "../utilities/application";

export default {
  name: 'Sustainability',
  props: {
    animal: String,
    selectedProduct: String,
    ppmDose: Number,
    fytDose: Number,
    selectedProductVersion: String,
    isAqua: Boolean,
  },
  data: () => ({
    feedProduction: 10000000
  }),

  methods: {
    billionNumberHandling(phoneEquiv) {
      if (phoneEquiv / 1000000000 > 1) {
        return `${Number.parseFloat(phoneEquiv / 1000000000).toFixed(2)} B`
      } else {
        return new Intl.NumberFormat().format(Math.round(phoneEquiv, 0))
      }
    },
  },


  computed: {
    annualEnzymeConsumption() {
        let factor = _.get(productFormMap[this.selectedProductVersion], this.selectedProduct, 10000) / 10000
        return factor * this.feedProduction * (this.ppmDose / 1000);
    },

    ghgEmissions: function() {
        return _.round((this.annualEnzymeConsumption / 10000) * 770);
    },

    po: function() {
        // Edited April 2023 for Aqua hiphorius only
        if (this.selectedProductVersion === 'hiphorius' && this.isAqua) {
            return -_.round((this.feedProduction / 1000 * 1.25) * aquaPhospohorousConstants[this.animal][_.round(this.fytDose / 250) * 250]);
        }
        return _.round((this.annualEnzymeConsumption / 10000) * 27.5);
    },

    car: function() {
        return _.round((this.annualEnzymeConsumption / 1000) * 2513198);
    },

    phone: function() {
        return _.round((this.annualEnzymeConsumption / 1000) * 121642646);
    },
    
    house: function() {
        return _.round((this.annualEnzymeConsumption / 1000) * 120);
    },
  }
};
</script>

<style scoped>
.align-sub {
  vertical-align: sub;
}
.img-arrow img {
  width: 16px;
  height: 22px;
}
.img-rotate img {
  transform: rotateX(180deg);
}
>>> .icon.help svg {
  @apply w-full h-full;
}
>>> .slither-slider {
  max-width: 353px;
  @apply mx-auto;
}
>>> .slither-slider-controls {
  @apply hidden;
}
>>> .slither-slider-dots {
  @apply bg-greenSustainability-light justify-center flex;
}
>>> .slither-slider-dots .slither-slider-dot {
  @apply bg-white border-grey-darkLight mx-1.5 mt-3 rounded-full;
  font-size: 0;
  width: 9px;
  height: 9px;
}
>>> .slither-slider-dots .slither-slider-dot.active-slide {
  @apply bg-green-light border-green-light;
}
</style>
